<script lang="ts">
	import { user } from '$lib/stores/userData'
	import type { PageData } from './$types'
	import JournalCard from '$lib/components/Cards/JournalCard.svelte'
	import type Journal from '$lib/types/journal'
	import { journalSize } from '$lib/utils/sizes'
	import { popup } from '$lib/utils/toast'
	import BookCard from '$lib/components/Cards/BookCard.svelte'
	import { Tab, TabPanel, TabPanels, TabGroup, TabList } from '@rgossiaux/svelte-headlessui'
	import { profile_lasttab } from '$lib/stores/navigation'
	export let data: PageData
	import PFP from '$lib/components/PFP.svelte'
	import { trpc } from '$lib/trpc/client'

	const getBookCount = (journal: Journal): number => journal.entryIDs.length ?? 0

	const unfollowUser = (userID: string) => {
		trpc().users.unfollow.mutate({ unfollowUserID: userID })
		data.isFollowing = !data.isFollowing
	}

	const followUser = (userID: string) => {
		trpc().users.follow.mutate({ followUserID: userID })
		data.isFollowing = !data.isFollowing
	}

	const onShare = () => {
		navigator.clipboard.writeText(window.location.href)
		popup('Share Link Copied')
	}
	const journalURL = (journal: Journal) => `/publicjournal/${journal.uuid}`

	// validate and correct a URL
	const fixupURL = (url: string) =>
		url.startsWith('http://') || url.startsWith('https://') ? url : 'https://' + url
</script>

<svelte:head>
	<title>User Profile</title>
</svelte:head>

<div class="pb-10 mx-auto text-center w-full pt-4 sm:max-w-7xl px-4 sm:px-6 lg:py-8 lg:px-8">
	<div class="flex flex-col justify-center w-full items-center">
		<profilecard
			class="flex flex-col justify-center w-full items-center bg-semilight text-light rounded-xl p-2 "
		>
			<PFP profile={data.userProfile} size={4} />
			<h1 class="text-4xl text-center">{data.userProfile.name}</h1>
			<username class="text-center ">
				@{data.userProfile.username}
			</username>
			<userstats class="cursor-pointer select-none flex items-center justify-center space-x-4 mt-2">
				<a
					href={`/users/${data.username}/follows?/followers`}
					class="font-bold text-blue-500 hover:underline"
				>
					{data.followerCount} Followers
				</a>
				<a
					href={`/users/${data.username}/follows?/following`}
					class="font-bold text-blue-500 hover:underline"
				>
					{data.followingCount} Following
				</a>
			</userstats>
			<buttons class="cursor-pointer select-none flex items-center justify-center space-x-2 mt-5">
				<button on:click={onShare} class="btn btn-app"> Share </button>
				{#if data.isOwnAccount}
					<a href="/profile" class="btn btn-black">Edit Profile</a>
				{:else if data.isFollowing && $user}
					<div class="w-full flex space-x-2 justify-center">
						<button
							type="button"
							class="flex-1 btn btn-app whitespace-nowrap"
							on:click={() => unfollowUser(data.userID)}
						>
							Unfollow
						</button>
					</div>
				{:else if $user}
					<div class="w-full flex space-x-2 justify-center">
						<button
							type="button"
							class="flex-1 btn btn-primary"
							on:click={() => followUser(data.userID)}
						>
							Follow
						</button>
					</div>
				{/if}
			</buttons>
			{#if data.userProfile.bio && data.userProfile.bio !== 'undefined'}
				<bio class="text-center mt-5">
					{data.userProfile.bio}
				</bio>
			{/if}
			{#if data.userProfile.links && data.userProfile.links !== 'undefined'}
				<links class="text-center text-gray-700 mt-5">
					<a class="text-blue-500 hover:underline" href={fixupURL(data.userProfile.links)}>
						{data.userProfile.links}
					</a>
				</links>
			{/if}
		</profilecard>
		<TabGroup defaultIndex={$profile_lasttab}>
			<TabList>
				<nav class="w-full flex space-x-5 text-lg mb-5 px-2 justify-center mt-4">
					<Tab
						class={({ selected }) =>
							selected
								? 'border-b-2 font-bold cursor-pointer border-black'
								: 'border-b-2 font-bold cursor-pointer border-transparent'}
						on:click={() => {
							$profile_lasttab = 0
						}}
					>
						Shared Journals
						<span class="text-gray-500 text-sm ">
							({data.journalCount})
						</span>
					</Tab>
					<Tab
						class={({ selected }) =>
							selected
								? 'border-b-2 font-bold cursor-pointer border-black'
								: 'border-b-2 font-bold cursor-pointer border-transparent'}
						on:click={() => {
							$profile_lasttab = 1
						}}
					>
						Liked Books
						<span class="text-gray-500 text-sm ">
							({data.likedBookCount})
						</span>
					</Tab>
				</nav>
			</TabList>
			<TabPanels>
				<TabPanel>
					<!-- {#if activePage === 'Shared Journals'} -->
					<h2 class="hidden mt-2">{data.journalCount} shared journals</h2>
					<div class="flex flex-wrap justify-center">
						{#each data.journals as journal}
							<a class={journalSize} href={journalURL(journal)}>
								<JournalCard {journal} bookCount={getBookCount(journal)} buttons={false} />
							</a>
						{/each}
					</div>
				</TabPanel>
				<TabPanel>
					<!-- {:else if activePage === 'Liked Books'} -->
					<div class="flex flex-wrap justify-center">
						{#each data.likedBooks as book}
							<a class="relative flex flex-col items-top mb-3 p-0.5" href={`/book/${book.uuid}`}>
								<BookCard {book} size="xs" showLikes={false} />
							</a>
						{/each}
					</div>
					<!-- {/if} -->
				</TabPanel>
			</TabPanels>
		</TabGroup>
	</div>
</div>
